import { Routes } from '@angular/router';
import { signedInGuard, verifiedGuard } from '@huddle-sports-platform/app-core';

import { adminInviteGuard, clubAdminGuard } from './data/admin-invite.guard';
import { adminTermsGuard, clubPaymentsLoadedGuard } from './data/club-payments.guard';
import { landingGuard } from './data/landing';
import { notificationsGuard } from './data/notifications.guard';
import { accountLoadedGuard, onboardedGuard } from './data/onboarded.guard';

/**
 * Ionic tab routing: the first segment after `tabs/` names the tab, and every
 * deeper segment lives on THAT tab's navigation stack.
 *
 * Every path here is FLAT and stays flat. On the web the club is the
 * ADDRESS - `dublinraiders.huddlesports.ie/sign-in` - and on the fallback
 * path form the app is mounted under the slug through `APP_BASE_HREF`, so
 * `/dublinraiders/sign-in` still reaches this table as `/sign-in` (see
 * `core/club-slug`). Nothing in this file, or in any guard or page, needs
 * to know which club it is serving.
 *
 * `/sign-in`, `/sign-up` and `/forgot-password` are the ungated doors (the
 * last is the way back in - it takes an address and sends Firebase's reset
 * mail, so it must reach someone who cannot sign in). Between them and the
 * app sit two landings:
 *
 * - `/verify-email` - signed in, email not yet verified; owns the resend and
 *   the verification poll, needs presence only.
 * - `/complete-your-profile` - verified but the account basics were never
 *   collected; owns the one-step profile form (which also JOINS the club -
 *   every build is a club's own app, so there is no join gate and no club
 *   picker). A NUDGE, not a wall: Landing sends a signed-in member here
 *   first, and the page's back button waves it away for the session.
 *
 * - `/enable-notifications` - the one ask the app makes of a member, on the
 *   way in (native builds only, and only while the system will still show
 *   its sheet - see data/notifications.guard).
 *
 * **There is no profile GUARD.** An incomplete profile used to bounce off
 * every signed-in route; it no longer does. The ask is made once on the
 * way in (Landing) and can be dismissed, and what an unnamed account
 * cannot do is enforced where the consequence actually is: `joinClub`
 * refuses an un-onboarded account server-side, so no club is joined and
 * nothing is registered, and the welcome page says so in place of its
 * Register button. A guard would only have added a wall in front of pages
 * - my account, settings - that a half-finished member has every right to
 * reach.
 *
 * The tab shell sits behind all of it: the kit's verified guard (presence +
 * verified email, still no claims - hq's adminGuard is the console's gate),
 * then the app's onboarded guard, which ALSO auto-joins an already-onboarded
 * account that is not yet a user of this club (see data/onboarded.guard),
 * and last the notifications guard. Deep links bounce: signed out
 * `** -> tabs/home -> /sign-in`; unverified `-> /verify-email`; never asked
 * about notifications `-> /enable-notifications`.
 */
export const routes: Routes = [
  {
    path: 'sign-in',
    loadComponent: () => import('./pages/sign-in.page').then((m) => m.SignInPage),
  },
  {
    // The PUBLIC payment page: a sponsor's invoice link, or a club's
    // fundraising drive. Deliberately outside every guard - the person
    // opening it has no account and may never have heard of Huddle, and
    // the token in the URL is the whole credential (see give.page.ts).
    path: 'give/:token',
    loadComponent: () => import('./pages/give.page').then((m) => m.GivePage),
  },
  {
    // The club lottery's PUBLIC page - the parish poster, never a till:
    // the jackpot and the price, and one door ("sign in to enter" - the
    // age gate needs a stored date of birth). Same reasoning as /give -
    // outside every guard, the token is the whole credential.
    path: 'lottery/:token',
    loadComponent: () => import('./pages/lottery-link.page').then((m) => m.LotteryLinkPage),
  },
  {
    // The e-ticket itself: the member's pass, the gate's scan target and
    // the printable web ticket, all one URL. Same reasoning as /give -
    // outside every guard, the token is the whole credential.
    path: 'ticket/:token',
    loadComponent: () => import('./pages/ticket.page').then((m) => m.TicketPage),
  },
  {
    path: 'sign-up',
    loadComponent: () => import('./pages/sign-up.page').then((m) => m.SignUpPage),
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('./pages/forgot-password.page').then((m) => m.ForgotPasswordPage),
  },
  {
    // Where Firebase's own account emails land: one URL carrying the job in
    // its query string (?mode=verifyEmail|resetPassword&oobCode=...), set as
    // the action URL in the Firebase console. Ungated by definition - a
    // reset link has to reach someone who cannot sign in, and a
    // verification link someone whose email is not verified yet.
    path: 'auth/action',
    loadComponent: () => import('./pages/auth-action.page').then((m) => m.AuthActionPage),
  },
  {
    // The theme ask - the first screen of a fresh sign-up's walk-in
    // (Landing sends them here ahead of the profile). Verified only; the
    // page persists the choice, so it never traps: any Continue records
    // a look and the landing rules move past it for good.
    path: 'choose-theme',
    loadComponent: () => import('./pages/choose-theme.page').then((m) => m.ChooseThemePage),
    canActivate: [verifiedGuard],
  },
  {
    path: 'verify-email',
    loadComponent: () => import('./pages/verify-email.page').then((m) => m.VerifyEmailPage),
    canActivate: [signedInGuard],
  },
  {
    // The beat after the code lands: a full page, not a toast. Verified
    // only - this moment comes before the profile ask; its Continue hands
    // over to the landing rules, which pick the profile form or a waiting
    // invitation.
    path: 'account-verified',
    loadComponent: () => import('./pages/account-verified.page').then((m) => m.AccountVerifiedPage),
    canActivate: [verifiedGuard],
  },
  {
    // The profile form - where Landing sends a signed-in member whose
    // account has no name yet, and where the welcome page points them.
    // accountLoadedGuard holds activation for the account snapshot so the
    // page knows synchronously what is left to collect (and can prefill
    // whatever IS there).
    path: 'complete-your-profile',
    loadComponent: () =>
      import('./pages/complete-your-profile.page').then((m) => m.CompleteYourProfilePage),
    canActivate: [verifiedGuard, accountLoadedGuard],
  },
  {
    // "What is your role at the club?" - straight after the profile form,
    // because the profile says WHO and this says what they are TO the club,
    // and every screen past here (plan criteria, the roster) reads the
    // second. Not behind roleChosenGuard - that would bounce this page off
    // itself.
    path: 'your-role',
    loadComponent: () => import('./pages/your-role.page').then((m) => m.YourRolePage),
    canActivate: [verifiedGuard],
  },
  {
    // The notifications ask. Deliberately NOT behind onboardedGuard (its
    // auto-join is not this page's business) and not behind
    // notificationsGuard (it would bounce this page off itself). Landing
    // still asks for the profile first: the ask is made of a person.
    path: 'enable-notifications',
    loadComponent: () =>
      import('./pages/enable-notifications.page').then((m) => m.EnableNotificationsPage),
    canActivate: [verifiedGuard],
  },
  {
    // The role offer. No onboardedGuard (the auto-join is not this page's
    // business); adminInviteGuard would bounce this page off itself.
    // Landing puts the profile ask ahead of the invitation, so in practice
    // the offer is read by somebody who has already been asked their name.
    path: 'admin-invite',
    loadComponent: () => import('./pages/admin-invite.page').then((m) => m.AdminInvitePage),
    canActivate: [verifiedGuard],
  },
  {
    // The admin area - the hq-scaffold shell (floating pill bar, glass
    // header, sticky sub-tabs) mounted one segment down; its children live
    // in shell/admin-shell.routes.ts. Same chain as the tabs plus the
    // claim check, so an admin who has not finished onboarding still
    // starts at /complete-your-profile. NOTHING diverts an admin away from
    // here: the Finance screen's "Get up & running" checklist carries
    // whatever is unfinished, which is a better door than a forced wizard.
    // Repeated as canActivateChild for deep links into the sections,
    // mirroring /tabs.
    path: 'admin',
    loadComponent: () => import('./shell/admin-shell').then((m) => m.AdminShell),
    canActivate: [verifiedGuard, onboardedGuard, clubAdminGuard, adminTermsGuard],
    canActivateChild: [verifiedGuard, onboardedGuard, clubAdminGuard, adminTermsGuard],
    loadChildren: () => import('./shell/admin-shell.routes').then((m) => m.adminChildren),
  },
  {
    // Settings - a page in its own right, not a tab: reached from the
    // welcome, the member app and the admin side nav, and it wears
    // my-account's chrome so the two read as a pair.
    path: 'settings',
    loadComponent: () => import('./pages/settings.page').then((m) => m.SettingsPage),
    canActivate: [verifiedGuard, onboardedGuard],
  },
  {
    // The signed-in person's own details - reached from Settings;
    // Back and Done return there. Behind the profile guard (it lists the
    // profile); no other gate - an account is yours whatever the club says.
    path: 'my-account',
    loadComponent: () => import('./pages/my-account.page').then((m) => m.MyAccountPage),
    canActivate: [verifiedGuard],
  },
  {
    // The writable face of My account: first name, last name, date of
    // birth, behind the progress-stage save. Reached from My account's
    // more menu; Back and a successful save both return there.
    path: 'edit-account',
    loadComponent: () => import('./pages/edit-account.page').then((m) => m.EditAccountPage),
    canActivate: [verifiedGuard],
  },
  {
    // The reauthentication gate in front of account deletion: a fresh
    // password on a full-screen page, then the removal itself. Reached
    // only from My account's confirmed menu choice.
    path: 'delete-account',
    loadComponent: () => import('./pages/delete-account.page').then((m) => m.DeleteAccountPage),
    canActivate: [verifiedGuard],
  },
  {
    // The setup-payments wizard - a full-screen flow, NOT a screen inside
    // the admin nav (docs/club-onboarding.md). Reached from the Home
    // checklist and the status banner; admins only.
    path: 'setup-your-payments',
    loadComponent: () =>
      import('./pages/setup-your-payments.page').then((m) => m.SetupYourPaymentsPage),
    canActivate: [verifiedGuard, onboardedGuard, clubAdminGuard, clubPaymentsLoadedGuard],
  },
  {
    // Huddle's terms with whoever runs the club: readable at any time,
    // and the door the bank flow will not open without.
    path: 'admin-terms',
    loadComponent: () => import('./pages/admin-terms.page').then((m) => m.AdminTermsPage),
    canActivate: [verifiedGuard, onboardedGuard, clubAdminGuard, clubPaymentsLoadedGuard],
  },
  {
    // The provider's embedded form on its own page, under one slim bar -
    // the status page above is the way in and the way back. No terms
    // guard any more: administrator terms move to sign-up (parked), and
    // routing a club to a terms page it never asked for read as a wrong
    // turn.
    path: 'connect-your-bank-account',
    loadComponent: () =>
      import('./pages/connect-your-bank-account.page').then((m) => m.ConnectYourBankAccountPage),
    canActivate: [verifiedGuard, onboardedGuard, clubAdminGuard, clubPaymentsLoadedGuard],
  },
  {
    // The follower landing - full-bleed club-colour welcome for a login
    // with no registrations yet (data/landing.ts). The member tabs stay
    // reachable; this is a landing, not a cage.
    path: 'welcome',
    loadComponent: () => import('./pages/welcome.page').then((m) => m.WelcomePage),
    canActivate: [verifiedGuard, onboardedGuard, adminInviteGuard, notificationsGuard],
  },
  {
    path: 'tabs',
    loadComponent: () => import('./tabs.page').then((m) => m.TabsPage),
    canActivate: [
      verifiedGuard,
      onboardedGuard,
      adminInviteGuard,
      notificationsGuard,
    ],
    canActivateChild: [
      verifiedGuard,
      onboardedGuard,
      adminInviteGuard,
      notificationsGuard,
    ],
    children: [
      {
        path: 'home',
        loadComponent: () => import('./pages/home.page').then((m) => m.HomePage),
      },
      {
        path: 'payments',
        loadComponent: () => import('./pages/payments.page').then((m) => m.PaymentsPage),
      },
      { path: '', redirectTo: 'home', pathMatch: 'full' },
    ],
  },
  // No route for the club slug: on the web the app is MOUNTED under it
  // (core/club-slug + APP_BASE_HREF), so the router never sees that segment
  // - `app.huddlesports.ie/dublinraiders/sign-in` arrives here as
  // `/sign-in`, and every route below is the club's as much as Huddle's.
  //
  // THE FRONT DOOR ASKS. It used to redirect straight to `tabs/home`, which
  // meant a REFRESH bypassed every landing rule: an admin reloading the app
  // arrived in the member view with no way back, and an unanswered
  // invitation sat unseen. `landingGuard` always answers with a UrlTree, so
  // nothing ever renders here.
  { path: '', pathMatch: 'full', canActivate: [landingGuard], children: [] },
  { path: '**', redirectTo: '' },
];
